/**
 * The command palette (TMMPDM-0040, IA-006 / SEARCH-001…005 / UI-011): one overlay, opened from the
 * top bar's "Search or jump to…" control or Ctrl/Cmd+K, that jumps to a tenant, a reseller, a
 * screen or a recent record, or runs a command. It accelerates; the nav stays the canonical path.
 *
 * A top-anchored `sm` dialog holding one combobox input and a grouped listbox, driven by
 * `aria-activedescendant` (A11Y-001/006): ↑/↓ move across groups, Home/End jump, Enter activates,
 * Esc closes and MUI returns focus to whatever had it. The palette itself is transient — it is
 * never in the URL; activating a result navigates (a normal history entry). The body mounts per
 * open, so recents are re-read each time.
 */
import { useCallback, useEffect, useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogTitle,
  IconButton,
  InputBase,
  LinearProgress,
  Tooltip,
  Typography,
  useColorScheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ContrastIcon from '@mui/icons-material/Contrast';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import { tenantPartnerPath } from '@/routes/partners/PartnerRedirects';
import { useCopy } from '@/hooks/useCopy';
import { tenantStatusLabel } from '@/domain/registry';
import { StatusChip } from '@/status';
import { usePaletteSources, type PaletteItem } from './usePaletteSources';
import { useRecent, type RecentEntry } from './useRecent';

/** An affiliate recent's key is `<slug>/<id>` (TMMPDM-0042). */
function recentPath(entry: RecentEntry): string {
  if (entry.type === 'tenant') return tenantPartnerPath(entry.key);
  if (entry.type === 'reseller' || entry.type === 'partner')
    return `/partners/${encodeURIComponent(entry.key)}`;
  const slash = entry.key.lastIndexOf('/');
  const slug = slash > 0 ? entry.key.slice(0, slash) : entry.key;
  const id = slash > 0 ? entry.key.slice(slash + 1) : '';
  return tenantPartnerPath(slug, `/affiliates/${encodeURIComponent(id)}`);
}

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

/** ⌘K on Apple platforms, Ctrl K elsewhere — only the hint differs; both chords open it anywhere. */
function isApplePlatform(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
}

interface PaletteTriggerProps {
  /** Below md: an icon button with the same name, so the top bar never overflows. */
  compact: boolean;
  onOpen: () => void;
}

/** The top bar's search control: looks like a field, is a button (SEARCH-003 — not a list filter). */
export function PaletteTrigger({ compact, onOpen }: PaletteTriggerProps) {
  const { t } = useCopy();
  const apple = isApplePlatform();
  const text = t('palette.trigger', 'Search or jump to…');
  const label = t('palette.triggerLabel', 'Search or jump to… ({{shortcut}})', {
    shortcut: apple ? '⌘K' : 'Ctrl+K',
  });
  if (compact) {
    return (
      <Tooltip title={text}>
        <IconButton onClick={onOpen} aria-label={label} aria-haspopup="dialog">
          <SearchIcon />
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <ButtonBase
      onClick={onOpen}
      aria-label={label}
      aria-haspopup="dialog"
      sx={{
        mr: 1,
        px: 1.25,
        py: 0.75,
        gap: 1,
        minWidth: 240,
        justifyContent: 'flex-start',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        color: 'text.secondary',
        bgcolor: 'background.paper',
        '&:hover': { borderColor: 'text.primary' },
        '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineStyle: 'solid' },
      }}
    >
      <SearchIcon fontSize="small" aria-hidden="true" />
      <Typography variant="body2" component="span" sx={{ flex: 1, textAlign: 'left' }}>
        {text}
      </Typography>
      <Box
        component="kbd"
        aria-hidden="true"
        sx={{
          typography: 'caption',
          fontFamily: 'inherit',
          px: 0.75,
          border: 1,
          borderColor: 'divider',
          borderRadius: 0.5,
        }}
      >
        {apple ? '⌘K' : 'Ctrl K'}
      </Box>
    </ButtonBase>
  );
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      slotProps={{ paper: { sx: { mt: { xs: 2, md: 10 }, mx: { xs: 2, md: 4 } } } }}
    >
      <PaletteBody onClose={onClose} />
    </Dialog>
  );
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const { t } = useCopy();
  const navigate = useNavigate();
  const { setMode } = useColorScheme();
  const { entries } = useRecent();
  const [input, setInput] = useState('');
  const view = usePaletteSources(input, entries);
  const items = view.groups.flatMap((group) => group.items);
  const [activeId, setActiveId] = useState<string | null>(null);
  // The selection follows the item, not a position: results arriving don't move it.
  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.id === activeId),
  );
  const active = items[activeIndex] ?? null;
  const baseId = useId();
  // The input takes focus as the palette opens (typing starts at once); MUI keeps it in the dialog.
  const focusOnMount = useCallback((el: HTMLInputElement | null) => el?.focus(), []);
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;
  const activeOptionId = active ? optionId(activeIndex) : undefined;

  useEffect(() => {
    if (!activeOptionId) return;
    document.getElementById(activeOptionId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeOptionId]);

  const go = (to: string) => {
    onClose();
    void navigate(to);
  };

  const activate = (item: PaletteItem) => {
    switch (item.kind) {
      case 'partner':
        return go(`/partners/${item.reseller.id}`);
      case 'customerRateCard':
        return go(`/rate-cards/${item.cardId}`);
      case 'recent':
        return go(recentPath(item.entry));
      case 'screen':
      case 'command':
      case 'listSearch':
        return go(item.to);
      case 'theme':
        setMode(item.mode);
        return onClose();
      case 'retry':
        return view.retry(item.source);
    }
  };

  const move = (index: number) => {
    const item = items[index];
    if (item) setActiveId(item.id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (items.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        return move((activeIndex + 1) % items.length);
      case 'ArrowUp':
        event.preventDefault();
        return move((activeIndex - 1 + items.length) % items.length);
      case 'Home':
        event.preventDefault();
        return move(0);
      case 'End':
        event.preventDefault();
        return move(items.length - 1);
      case 'Enter':
        event.preventDefault();
        if (active) activate(active);
        return;
    }
  };

  const title = t('palette.trigger', 'Search or jump to…');
  const resultCount = items.filter((item) => item.kind !== 'retry').length;
  const searching = view.groups
    .filter((group) => group.loading)
    .map((group) =>
      group.id === 'customerRateCards'
        ? t('rateCard.searching', 'Searching rate cards')
        : t('partners.searching', 'Searching partners'),
    )
    .join(' · ');
  // The status row sits outside the listbox: the search in progress, then its outcome.
  const announcement =
    view.term === ''
      ? ''
      : !view.settled
        ? searching
        : view.noMatches
          ? t('palette.noMatches', 'No matches for "{{q}}"', { q: view.term })
          : t('palette.resultCount', '{{count}} results', { count: resultCount });

  const indexOf = new Map(items.map((item, i) => [item.id, i]));
  return (
    <>
      <DialogTitle sx={visuallyHidden}>{title}</DialogTitle>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <SearchIcon aria-hidden="true" sx={{ color: 'text.secondary' }} />
        <InputBase
          inputRef={focusOnMount}
          fullWidth
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={title}
          slotProps={{
            input: {
              role: 'combobox',
              'aria-label': title,
              // Expanded only while the listbox has options to move through (A11Y-001).
              'aria-expanded': items.length > 0,
              'aria-controls': listboxId,
              'aria-autocomplete': 'list',
              ...(activeOptionId ? { 'aria-activedescendant': activeOptionId } : {}),
            },
          }}
        />
      </Box>
      <Box component="span" role="status" sx={visuallyHidden}>
        {announcement}
      </Box>
      {view.noMatches ? (
        <Typography variant="body2" sx={{ px: 2, pt: 2 }}>
          {t('palette.noMatches', 'No matches for "{{q}}"', { q: view.term })}
        </Typography>
      ) : null}
      <Box
        id={listboxId}
        role="listbox"
        aria-label={title}
        sx={{ maxHeight: { xs: '60vh', md: 440 }, overflowY: 'auto', py: 1 }}
      >
        {view.groups.map((group) => {
          const labelId = `${baseId}-group-${group.id}`;
          return (
            <Box key={group.id} role="group" aria-labelledby={labelId} sx={{ pb: 0.5 }}>
              <Typography
                id={labelId}
                variant="caption"
                component="div"
                sx={{ px: 2, pt: 1, pb: 0.5, fontWeight: 600, color: 'text.secondary' }}
              >
                {group.label}
              </Typography>
              {/* Visual only: a listbox holds options, so the status row names the search. */}
              {group.loading ? (
                <LinearProgress aria-hidden="true" sx={{ mx: 2, my: 1, height: 2 }} />
              ) : null}
              {group.items.map((item) => {
                const index = indexOf.get(item.id) ?? 0;
                const selected = item.id === active?.id;
                return (
                  <Box
                    key={item.id}
                    id={optionId(index)}
                    role="option"
                    tabIndex={-1}
                    aria-selected={selected}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => activate(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      bgcolor: selected ? 'action.selected' : 'transparent',
                      boxShadow: (th) =>
                        selected ? `inset 3px 0 0 ${(th.vars ?? th).palette.primary.main}` : 'none',
                      '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
                    }}
                  >
                    <OptionContent item={item} />
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
      <Typography
        variant="caption"
        component="div"
        sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', color: 'text.secondary' }}
      >
        {t('palette.keysHint', '↑↓ to move · Enter to open · Esc to close')}
      </Typography>
    </>
  );
}

function OptionIcon({ children }: { children: ReactNode }) {
  return (
    <Box
      aria-hidden="true"
      sx={{ display: 'flex', color: 'text.secondary', '& svg': { fontSize: 20 } }}
    >
      {children}
    </Box>
  );
}

function TypeLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      component="span"
      sx={{ minWidth: 64, color: 'text.secondary', textTransform: 'uppercase' }}
    >
      {children}
    </Typography>
  );
}

/** Name, then what tells similar names apart: slug, status (as the server sent it), reseller. */
function OptionContent({ item }: { item: PaletteItem }) {
  const { t } = useCopy();
  const resellerType = t('partners.singular', 'Partner');
  const affiliateType = t('palette.type.affiliate', 'Affiliate');
  switch (item.kind) {
    case 'customerRateCard':
      return (
        <>
          <TypeLabel>{t('rateCard.singular', 'Rate card')}</TypeLabel>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {item.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {item.currency}
            </Typography>
          </Box>
        </>
      );
    case 'partner':
      return (
        <>
          <TypeLabel>{resellerType}</TypeLabel>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {item.reseller.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              <Box component="span" sx={(theme) => theme.typography.mono}>
                {item.reseller.slug}
              </Box>
              {item.reseller.tenant ? ` · ${item.reseller.tenant.name}` : ''}
            </Typography>
          </Box>
          <StatusChip
            status={item.reseller.status}
            label={tenantStatusLabel(item.reseller.status, t)}
          />
        </>
      );
    case 'recent':
      return (
        <>
          <OptionIcon>
            <HistoryIcon />
          </OptionIcon>
          <TypeLabel>{item.entry.type === 'affiliate' ? affiliateType : resellerType}</TypeLabel>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {item.entry.label}
            </Typography>
            {item.entry.type === 'tenant' || item.entry.type === 'affiliate' ? (
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {item.entry.key}
              </Typography>
            ) : null}
          </Box>
        </>
      );
    case 'screen':
      return (
        <>
          <OptionIcon>{item.icon}</OptionIcon>
          <Typography variant="body2">{item.label}</Typography>
        </>
      );
    case 'command':
    case 'theme':
      return (
        <>
          <OptionIcon>{item.kind === 'theme' ? <ContrastIcon /> : <AddIcon />}</OptionIcon>
          <Typography variant="body2">{item.label}</Typography>
        </>
      );
    case 'retry':
      return (
        <>
          <OptionIcon>
            <RefreshIcon />
          </OptionIcon>
          <Typography variant="body2">{item.label}</Typography>
        </>
      );
    case 'listSearch':
      return (
        <>
          <OptionIcon>
            <SearchIcon />
          </OptionIcon>
          <Typography variant="body2">{item.label}</Typography>
        </>
      );
  }
}
